// Dunne laag over de publieke API. Netwerk- en serverfouten worden een Error;
// 409 (al gekozen) en 503 (lijst niet klaar) zijn een gewone CheckoutResult.
import type { AdminGift, CsvImportResult, CsvSuggestion, AdminOrder, AdminPageImage, PageImageKind, CheckoutRequest, CheckoutResult, GiftInput, ListResponse, OrderStatus, OrderView, SettingsInput } from '../shared/api-types.ts';

const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

async function request(url: string, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(url, { cache: 'no-store', ...init });
  } catch {
    throw new Error('Geen verbinding met de server');
  }
}

function fail(res: Response): never {
  throw new Error(`Onverwacht antwoord van de server (${res.status})`);
}

export async function getList(): Promise<ListResponse> {
  const res = await request('/api/list', { headers: { Accept: 'application/json' } });
  if (!res.ok) fail(res);
  return (await res.json()) as ListResponse;
}

/** 429 op het bestellen: te veel bestellingen vanaf dit netwerk. */
export class RateLimited extends Error {
  constructor() {
    super('Even rustig: er werden net veel bestellingen geplaatst vanaf dit netwerk. Probeer het over een paar minuten opnieuw.');
  }
}

export async function postOrder(req: CheckoutRequest): Promise<CheckoutResult> {
  const res = await request('/api/orders', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(req) });
  if (res.status === 201) return { ok: true, order: (await res.json()) as OrderView };
  if (res.status === 409) {
    const body = (await res.json()) as { giftIds?: unknown };
    const giftIds = Array.isArray(body.giftIds) ? body.giftIds.filter((x): x is string => typeof x === 'string') : [];
    return { ok: false, code: 'unavailable', giftIds };
  }
  if (res.status === 503) return { ok: false, code: 'not_ready' };
  if (res.status === 429) throw new RateLimited();
  return fail(res);
}

export async function getOrder(token: string): Promise<OrderView | null> {
  const res = await request(`/api/orders/${encodeURIComponent(token)}`, { headers: { Accept: 'application/json' } });
  if (res.status === 404) return null;
  if (!res.ok) fail(res);
  return (await res.json()) as OrderView;
}

export async function claimPaid(token: string): Promise<void> {
  const res = await request(`/api/orders/${encodeURIComponent(token)}/paid`, { method: 'POST' });
  if (res.status !== 204) fail(res);
}

// ---- beheer: cookiesessie, alles onder /api/admin ----

/** De sessie is verlopen of ontbreekt: toon het loginscherm opnieuw. */
export class Unauthorized extends Error {
  constructor() {
    super('Niet ingelogd');
  }
}

async function admin(path: string, init: RequestInit = {}): Promise<Response> {
  const res = await request(`/api/admin${path}`, { credentials: 'same-origin', ...init });
  if (res.status === 401) throw new Unauthorized();
  return res;
}

function jsonInit(method: string, body: unknown): RequestInit {
  return { method, headers: JSON_HEADERS, body: JSON.stringify(body) };
}

export type LoginResult = 'ok' | 'wrong' | 'limited';

export async function login(password: string): Promise<LoginResult> {
  const res = await request('/api/admin/login', { credentials: 'same-origin', ...jsonInit('POST', { password }) });
  if (res.status === 204) return 'ok';
  if (res.status === 401) return 'wrong';
  if (res.status === 429) return 'limited';
  return fail(res);
}

export async function logout(): Promise<void> {
  await request('/api/admin/logout', { method: 'POST', credentials: 'same-origin' });
}

export async function hasSession(): Promise<boolean> {
  const res = await request('/api/admin/me', { credentials: 'same-origin' });
  return res.status === 204;
}

async function adminJson<T>(path: string): Promise<T> {
  const res = await admin(path, { headers: { Accept: 'application/json' } });
  if (!res.ok) fail(res);
  return (await res.json()) as T;
}

async function expect204(res: Response): Promise<void> {
  if (res.status !== 204) fail(res);
}

export const listAdminGifts = (): Promise<AdminGift[]> => adminJson('/gifts');
export const listAdminOrders = (): Promise<AdminOrder[]> => adminJson('/orders');
export const getAdminSettings = (): Promise<SettingsInput> => adminJson('/settings');

export async function saveGift(input: GiftInput, id: string | null): Promise<AdminGift | 'has_contributions'> {
  const res = await admin(id === null ? '/gifts' : `/gifts/${encodeURIComponent(id)}`, jsonInit(id === null ? 'POST' : 'PUT', input));
  if (res.status === 409) return 'has_contributions';
  if (!res.ok) fail(res);
  return (await res.json()) as AdminGift;
}

export async function deleteGift(id: string): Promise<'ok' | 'has_contributions'> {
  const res = await admin(`/gifts/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (res.status === 409) return 'has_contributions';
  await expect204(res);
  return 'ok';
}

export async function saveGiftOrder(ids: string[]): Promise<void> {
  await expect204(await admin('/gifts/order', jsonInit('PUT', { ids })));
}

export async function uploadGiftImage(id: string, image: Blob): Promise<'ok' | 'too_large' | 'unsupported'> {
  const body = new FormData();
  body.append('image', image, 'foto.jpg');
  const res = await admin(`/gifts/${encodeURIComponent(id)}/image`, { method: 'PUT', body });
  if (res.status === 413) return 'too_large';
  if (res.status === 415) return 'unsupported';
  if (!res.ok) fail(res);
  return 'ok';
}

export async function deleteGiftImage(id: string): Promise<void> {
  await expect204(await admin(`/gifts/${encodeURIComponent(id)}/image`, { method: 'DELETE' }));
}

export async function setOrderStatus(id: string, status: OrderStatus): Promise<void> {
  await expect204(await admin(`/orders/${encodeURIComponent(id)}/status`, jsonInit('PUT', { status })));
}

export async function deleteOrder(id: string): Promise<void> {
  await expect204(await admin(`/orders/${encodeURIComponent(id)}`, { method: 'DELETE' }));
}

export async function importCsv(file: File): Promise<CsvImportResult> {
  const body = new FormData();
  body.append('file', file);
  const res = await admin('/orders/import-csv', { method: 'POST', body });
  if (res.status === 413) throw new Error('Dat bestand is te groot');
  if (!res.ok) fail(res);
  const json = (await res.json()) as { matched?: unknown; suggestions?: unknown };
  return {
    matched: typeof json.matched === 'number' ? json.matched : 0,
    suggestions: Array.isArray(json.suggestions) ? (json.suggestions as CsvSuggestion[]) : [],
  };
}

export async function saveSettings(input: SettingsInput): Promise<'ok' | 'invalid_iban'> {
  const res = await admin('/settings', jsonInit('PUT', input));
  if (res.status === 400) return 'invalid_iban';
  await expect204(res);
  return 'ok';
}

export const listCategories = (): Promise<string[]> => adminJson('/categories');
export const listPageImages = (): Promise<AdminPageImage[]> => adminJson('/page-images');

export async function uploadPageImage(kind: PageImageKind, image: Blob, caption: string): Promise<'ok' | 'too_large' | 'unsupported'> {
  const body = new FormData();
  body.append('kind', kind);
  body.append('caption', caption);
  body.append('image', image, 'foto.jpg');
  const res = await admin('/page-images', { method: 'POST', body });
  if (res.status === 413) return 'too_large';
  if (res.status === 415) return 'unsupported';
  if (!res.ok) fail(res);
  return 'ok';
}

export async function savePageImageCaption(id: string, caption: string): Promise<void> {
  await expect204(await admin(`/page-images/${encodeURIComponent(id)}`, jsonInit('PUT', { caption })));
}

export async function deletePageImage(id: string): Promise<void> {
  await expect204(await admin(`/page-images/${encodeURIComponent(id)}`, { method: 'DELETE' }));
}

export async function savePageImageOrder(kind: PageImageKind, ids: string[]): Promise<void> {
  await expect204(await admin('/page-images/order', jsonInit('PUT', { kind, ids })));
}
